import { useAuth } from "@/lib/contexts/auth.context";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, AlertTriangle } from "lucide-react";
import PlatformManagerOrganizationsService from "@/lib/platformManager/platformManagerOrganization.service";
import { useNavigate } from "react-router-dom";

/**
 * This page is the platform manager dashboard
 * - links to the organization management page
 * - allows to reset the test data of the taxpeer organization
 * @returns
 */
export const PlatformManagerDashboardPage = () => {
	const { organizations, currentOrganization, setCurrentOrganization } =
		useAuth();
	const navigate = useNavigate();

	const [showResetDialog, setShowResetDialog] = useState(false);
	const [isResetting, setIsResetting] = useState(false);
	const [resetError, setResetError] = useState<string | null>(null);

	const service = useMemo(
		() => PlatformManagerOrganizationsService.getInstance(),
		[],
	);

	/**
	 * This is called when the user wants to reset the test data of the taxpeer organization
	 */
	const handleResetTestData = async () => {
		setIsResetting(true);
		setResetError(null);
		try {
			await service.resetTestData();
			setShowResetDialog(false);
			// Optionally reload the page or refresh data here
			window.location.reload();
		} catch (_error) {
			setResetError(
				"Fehler beim Zurücksetzen der Testdaten. Bitte versuche es erneut.",
			);
		} finally {
			setIsResetting(false);
		}
	};

	return (
		<div className="container mx-auto max-w-5xl p-4 sm:p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-semibold">Platform Manager Dashboard</h1>
				<p className="text-muted-foreground">
					Manage organizations for the platform
				</p>
			</div>
			<div className="mb-4 flex items-center gap-3">
				<span className="text-sm text-muted-foreground">
					Current organization:
				</span>
				<Select
					value={currentOrganization?.organizationId}
					onValueChange={(value) => setCurrentOrganization(value)}
				>
					<SelectTrigger>
						<SelectValue placeholder="Select organization" />
					</SelectTrigger>
					<SelectContent>
						{organizations.map((o) => (
							<SelectItem key={o.organizationId} value={o.organizationId}>
								{o.name || o.organizationId}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Reset Test Data Section */}
			<Card className="mb-6 border-destructive/20">
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-destructive">
						<AlertTriangle className="h-5 w-5" />
						Testdaten zurücksetzen
					</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-sm text-muted-foreground mb-4">
						Diese Aktion setzt alle Testdaten der aktuellen Organisation zurück.
						Diese Aktion kann nicht rückgängig gemacht werden.
					</p>
					<Button
						variant="destructive"
						onClick={() => setShowResetDialog(true)}
						className="flex items-center gap-2"
					>
						<RefreshCw className="h-4 w-4" />
						Testdaten zurücksetzen
					</Button>
					{resetError && (
						<div className="text-destructive mt-2 text-sm">{resetError}</div>
					)}
				</CardContent>
			</Card>

			{/* Link to Organization Management Page */}
			<Card className="mb-6">
				<CardHeader>
					<CardTitle>Organisationen verwalten</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-sm text-muted-foreground mb-4">
						Verwalte alle Organisationen der Plattform auf einer separaten
						Seite.
					</p>
					<Button onClick={() => navigate("/platformManager/organizations")}>
						Zur Organisationsverwaltung
					</Button>
				</CardContent>
			</Card>

			{/* Reset Confirmation Dialog */}
			<Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
				<DialogContent className="sm:max-w-[500px] bg-background-secondary">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-destructive">
							<AlertTriangle className="h-5 w-5" />
							Testdaten wirklich zurücksetzen?
						</DialogTitle>
						<DialogDescription>
							Diese Aktion wird alle Testdaten der aktuellen Organisation
							unwiderruflich löschen.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						<p className="text-sm text-muted-foreground">
							Die folgenden Daten werden gelöscht:
						</p>
						<ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
							<li>Alle Klausuren und Aufgaben</li>
							<li>Alle Korrekturen und Bewertungen</li>
							<li>Alle Benutzer-Zuweisungen</li>
							<li>Alle Einstellungen und Konfigurationen</li>
						</ul>
						<p className="font-semibold text-destructive text-sm">
							Diese Aktion kann nicht rückgängig gemacht werden!
						</p>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setShowResetDialog(false)}
							disabled={isResetting}
						>
							Abbrechen
						</Button>
						<Button
							variant="destructive"
							onClick={handleResetTestData}
							disabled={isResetting}
							className="flex items-center gap-2"
						>
							{isResetting ? (
								<>
									<RefreshCw className="h-4 w-4 animate-spin" />
									Wird zurückgesetzt...
								</>
							) : (
								<>
									<RefreshCw className="h-4 w-4" />
									Ja, zurücksetzen
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};
